import { useEffect, useCallback, useMemo } from 'react';
import { useStore } from '@/store/store';
import { POPUPS } from '@/app/components/popup/constants';
import type { ExclusiveZoneData } from '@/types/zones/exclusive-zone';
import { getPathFromLink } from '@/logic/page-config/navigation/navigation-adapter';
import { getPopupContent } from '@/app/components/popup/layouts/exclusive-popup/utils/get-popup-content';
import { getSeenZones } from '@/utils/exclusive-popup';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';

type UseExclusivePopupTriggerProps = {
    zoneData: ExclusiveZoneData;
    zoneUid: string;
    isMobile?: boolean;
    exclusiveType: string;
    exclusiveLaunchType: string;
};

export const useExclusivePopupTrigger = ({
    zoneData,
    zoneUid,
    isMobile = false,
    exclusiveType,
    exclusiveLaunchType
}: UseExclusivePopupTriggerProps) => {
    const { queuePopup } = useStore((state) => state.popup);
    const { isLoggedIn, featureFlags, region } = useGlobalContext();
    const isEarlyAccess = featureFlags[PRODUCT_THROTTLES.HAS_EARLY_ACCESS];

    const gameContent = zoneData?.select_game;
    const portraitLayoutContent = zoneData?.portrait?.[0];
    const categoryPath = zoneData?.category ? getPathFromLink(zoneData?.category) : '/';

    const showPopup = useCallback(() => {
        const exclusivePopupContent = getPopupContent({
            portraitLayoutContent,
            gameContent,
            categoryPath: categoryPath ?? '/',
            videoTrailerUrl: zoneData?.video_trailer?.url,
            launchDate: zoneData?.launch_date,
            description: zoneData?.description,
            exclusiveType,
            isEarlyAccess,
            region,
            zoneUid,
            exclusiveLaunchType
        });

        if (exclusivePopupContent) {
            queuePopup({ type: POPUPS.EXCLUSIVE_ZONE, params: exclusivePopupContent });
        }
    }, [
        portraitLayoutContent,
        gameContent,
        categoryPath,
        zoneData?.video_trailer?.url,
        zoneData?.launch_date,
        zoneData?.description,
        exclusiveType,
        isEarlyAccess,
        region,
        zoneUid,
        queuePopup
    ]);

    const shouldShow = useMemo(() => {
        const videoTrailerUrl = zoneData?.video_trailer?.url;
        const layoutContent = zoneData?.portrait?.[0];
        const hasAllContent = !!(layoutContent?.primary && layoutContent?.id && videoTrailerUrl && gameContent);

        if (!hasAllContent) {
            return false;
        }

        if (zoneData?.show_popup_at_login === false) {
            return false;
        }

        if (!isLoggedIn || !isMobile) {
            return false;
        }

        const seenZones = typeof window !== 'undefined' ? getSeenZones() : [];
        return !seenZones.includes(zoneUid);
    }, [
        zoneData?.video_trailer?.url,
        zoneData?.portrait,
        zoneData?.show_popup_at_login,
        gameContent,
        isLoggedIn,
        isMobile,
        zoneUid
    ]);

    useEffect(() => {
        if (shouldShow) {
            showPopup();
        }
    }, [shouldShow, showPopup]);

    return showPopup;
};
